import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { groupBy } from '../../../algorithms/grouping';
import { useLangFile } from '../../../context/LanguageContext';
import { useGoogleMap } from '../../../context/GoogleMapContext';
import { goToField } from '../../../helpers/MapsUtils';
import { useHookRef } from '../../../hooks/useHookRef';
import { CropType } from '../../../constants/CropConstants';
import FieldOverviewSection from '../../FieldOverviewList/FieldOverviewSection';
import BulkPrescriptionFieldOverview from './BulkPrescriptionFieldOverview';
import { areAllFieldsOfCropSelected } from '../BulkUtils';
import type Field from '../../../model/Field';

interface BulkPrescriptionFieldOverviewContainerProps extends ConnectedProps<typeof connector> {
  fields: Field[];
  selectedFields: Field[];
  setSelectedFields: (fields: Field[]) => void;
  onClose: () => void;
}

const BulkPrescriptionFieldOverviewContainer: React.FC<
  BulkPrescriptionFieldOverviewContainerProps
> = ({ fields, onClose, selectedField, selectedFields, setSelectedFields }) => {
  const LangFile = useLangFile();
  const googleMap = useGoogleMap();
  const googleMapRef = useHookRef(googleMap);
  const [sections, setSections] = useState<FieldOverviewSection[]>([]);
  const [filteredCrop, setFilteredCrop] = useState<string[]>([]);
  const grouped = useMemo(() => groupBy(fields, 'crop'), [fields]);

  const onFieldClicked = useCallback(
    (field: Field) => {
      goToField(googleMapRef.current, field);
    },
    [googleMapRef]
  );

  const onFieldChecked = useCallback(
    (field: Field, checked: boolean) => {
      let newSelectedFields: Field[];

      if (checked) {
        newSelectedFields = [...selectedFields, field];
      } else {
        newSelectedFields = selectedFields.filter(
          (fieldEntry) => fieldEntry.fieldId !== field.fieldId
        );
      }

      const allSelectedForCrop = areAllFieldsOfCropSelected(field.crop, newSelectedFields, grouped);

      let newFilteredCropTypes = filteredCrop;

      if (allSelectedForCrop && !filteredCrop.includes(field.crop)) {
        newFilteredCropTypes = [...filteredCrop, field.crop];
      }

      if (!allSelectedForCrop && filteredCrop.includes(field.crop)) {
        newFilteredCropTypes = filteredCrop.filter((cropType) => cropType !== field.crop);
      }

      setSelectedFields(newSelectedFields);
      setFilteredCrop(newFilteredCropTypes);
    },
    [selectedFields, filteredCrop, grouped, setSelectedFields]
  );

  const onCropTypeChecked = useCallback(
    (cropType: string, checked: boolean) => {
      if (checked) {
        const unselectedFields = fields.filter((field) => !selectedFields.includes(field));
        setSelectedFields([
          ...selectedFields,
          ...unselectedFields.filter((field) => field.crop === cropType),
        ]);
        setFilteredCrop([...filteredCrop, cropType]);
      } else {
        setSelectedFields(selectedFields.filter((field) => field.crop !== cropType));
        setFilteredCrop(filteredCrop.filter((cropTypeEntry) => cropTypeEntry !== cropType));
      }
    },
    [fields, selectedFields, filteredCrop, setSelectedFields]
  );

  const onClearFilter = () => {
    setSelectedFields([]);
    setFilteredCrop([]);
  };

  useEffect(() => {
    if (fields.length > 0) {
      const grouped = groupBy(fields, 'crop');

      const unsortedSections = Object.keys(grouped).map((cropType, idx) => {
        return new FieldOverviewSection(
          idx,
          LangFile.CropType[cropType],
          grouped[cropType],
          cropType
        );
      });

      const noneSectionIdx = unsortedSections.findIndex((item) => item.cropType === CropType.NONE);
      const noneSection = unsortedSections.splice(noneSectionIdx, 1)[0];

      const sortedSections = unsortedSections.sort((a, b) => a.header.localeCompare(b.header));
      sortedSections.push(noneSection);

      setSections(sortedSections);
    }
  }, [fields, LangFile]);

  useEffect(() => {
    // Add already selected crop types when opening field list
    const uniqueCrops = [...new Set(selectedFields.map((f) => f.crop))];

    setFilteredCrop((prev) => {
      const cropsToAdd = uniqueCrops.filter((crop) => {
        if (prev.includes(crop)) return false;
        return areAllFieldsOfCropSelected(crop, selectedFields, grouped);
      });

      return cropsToAdd.length > 0 ? [...prev, ...cropsToAdd] : prev;
    });
  }, [selectedFields, grouped]);

  return (
    <BulkPrescriptionFieldOverview
      selectedField={selectedField}
      selectedFields={selectedFields}
      sections={sections}
      onCloseClicked={onClose}
      onFieldClicked={onFieldClicked}
      onFieldChecked={onFieldChecked}
      onCropTypeChecked={onCropTypeChecked}
      onClearFilter={onClearFilter}
      filteredCropTypes={filteredCrop}
    />
  );
};

const mapStateToProps = (store: any) => ({
  selectedField: store.field.selectedField,
});

const connector = connect(mapStateToProps);
export default memo(connector(BulkPrescriptionFieldOverviewContainer));
